/**
 * Choix unique, une seule forme selon le nombre d'options : segmenté jusqu'à
 * trois options courtes, cartes quand une option mérite une phrase, liste
 * déroulante au-delà de six. `chips` (demandé explicitement) : beaucoup de
 * valeurs très courtes qu'on touche d'un geste (des durées), en pastilles qui
 * passent à la ligne. Choix multiple : CheckboxGroup.
 */
import { useId, type ReactNode } from 'react';

export interface Choice<T extends string> {
  value: T;
  label: string;
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

type Variant = 'auto' | 'segmented' | 'cards' | 'select' | 'chips';

export function choiceVariant<T extends string>(options: Choice<T>[], variant: Variant = 'auto'): Exclude<Variant, 'auto'> {
  if (variant !== 'auto') return variant;
  if (options.length > 6) return 'select';
  if (options.some((option) => option.hint || option.icon)) return 'cards';
  if (options.length <= 3 && options.every((option) => option.label.length <= 14)) return 'segmented';
  return 'cards';
}

export function ChoiceGroup<T extends string>(props: {
  label: string;
  value: T | null;
  onChange: (value: T) => void;
  options: Choice<T>[];
  variant?: Variant;
  /** Le libellé reste lu par les lecteurs d'écran, mais n'est pas affiché. */
  hideLabel?: boolean;
  /** Précision courte sous le libellé. */
  hint?: string | undefined;
  /** Message d'erreur du champ, lu par les lecteurs d'écran. */
  error?: string | undefined;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  const variant = choiceVariant(props.options, props.variant);
  const labelClass = props.hideLabel ? 'sr-only' : 'field-label';
  if (variant === 'select') {
    return (
      <label className="field">
        <span className={labelClass}>{props.label}</span>
        {props.hint && !props.hideLabel ? <span className="muted">{props.hint}</span> : null}
        <select
          value={props.value ?? ''}
          disabled={props.disabled}
          onChange={(event) => props.onChange(event.target.value as T)}
        >
          {props.value === null ? <option value="" disabled>Choisir…</option> : null}
          {props.options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        {props.error ? <span className="field-error" role="alert">{props.error}</span> : null}
      </label>
    );
  }
  return (
    <div className="ui-choice">
      <span className={labelClass} id={`${id}-label`}>
        {props.label}
      </span>
      {props.hint && !props.hideLabel ? <span className="muted">{props.hint}</span> : null}
      <div
        className={`ui-choice__options ui-choice__options--${variant}`}
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        onKeyDown={(event) => {
          const enabled = props.options.filter((option) => !option.disabled);
          const index = enabled.findIndex((option) => option.value === props.value);
          const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
          if (!step || enabled.length === 0) return;
          event.preventDefault();
          const next = enabled[(index + step + enabled.length) % enabled.length];
          if (next) {
            props.onChange(next.value);
            (event.currentTarget.querySelector(`[data-value="${CSS.escape(next.value)}"]`) as HTMLElement | null)?.focus();
          }
        }}
      >
        {props.options.map((option) => {
          const checked = option.value === props.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={checked}
              data-value={option.value}
              tabIndex={checked || (props.value === null && option === props.options[0]) ? 0 : -1}
              disabled={props.disabled || option.disabled}
              className="ui-choice__option"
              onClick={() => props.onChange(option.value)}
            >
              {option.icon ? <span className="ui-choice__icon" aria-hidden="true">{option.icon}</span> : null}
              <span className="ui-choice__text">
                <span className="ui-choice__label">{option.label}</span>
                {variant === 'cards' && option.hint ? <span className="ui-choice__hint">{option.hint}</span> : null}
              </span>
            </button>
          );
        })}
      </div>
      {props.error ? <span className="field-error" role="alert">{props.error}</span> : null}
    </div>
  );
}

/** Plusieurs choix possibles : cases à cocher dessinées, une par ligne. */
export function CheckboxGroup<T extends string>(props: {
  label: string;
  values: T[];
  onChange: (values: T[]) => void;
  options: Choice<T>[];
  disabled?: boolean;
}): ReactNode {
  return (
    <fieldset className="ui-checkboxes">
      <legend className="field-label">{props.label}</legend>
      {props.options.map((option) => {
        const checked = props.values.includes(option.value);
        return (
          <label key={option.value} className="ui-checkboxes__item">
            {/* La case dessinée fait 22 px ; la case réelle, invisible au-dessus, 44 px au doigt. */}
            <span className="ui-checkboxes__control">
              <input
                type="checkbox"
                checked={checked}
                disabled={props.disabled || option.disabled}
                onChange={() =>
                  props.onChange(checked ? props.values.filter((value) => value !== option.value) : [...props.values, option.value])
                }
              />
              <span className="ui-checkboxes__mark" aria-hidden="true" />
            </span>
            <span>
              <span className="ui-choice__label">{option.label}</span>
              {option.hint ? <span className="ui-choice__hint">{option.hint}</span> : null}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
